/**
 * Sempirical AuthProvider
 * =======================
 * The browser side of R-B (docs/remediation/R-B.md). The backend decides everything;
 * this file only holds the session and follows the backend's answers:
 *
 *   POST /api/auth/login     -> { token, refresh_token?, expires_in?, auth_method,
 *                                 mfa_required, mfa_enrolled, factor_id, role, ... }
 *   POST /api/auth/break-glass -> the emergency admin sign-in (/login?break-glass); same
 *                                 response shape, auth_method 'break_glass', no refresh
 *   POST /api/auth/mfa/verify, /mfa/enroll, GET /mfa/factors  (staff TOTP)
 *   POST /api/auth/refresh   -> a new session before the 15-minute access token expires
 *   POST /api/auth/logout    -> ends the Supabase session everywhere
 *   GET  /api/auth/me        -> role / customers from the users row (never assumed)
 *
 * RULES THIS FILE KEEPS
 *   - A missing role is the LEAST privileged role ('customer'), never 'admin'.
 *   - Any 401 from the API clears the stored session (after one refresh attempt when a
 *     refresh token exists). This is done by one wrapper around window.fetch, so the
 *     pages that call fetch() directly get it too -- not just src/api.js.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { API_BASE as API_BASE_CONFIG } from './config'

const AuthContext = createContext(null)

const API_BASE = API_BASE_CONFIG
export const TOKEN_KEY = 'xlr8_token'
const REFRESH_KEY = 'xlr8_refresh_token'
const EXPIRES_KEY = 'xlr8_token_expires_at'
export const SESSION_ENDED_EVENT = 'sempirical:session-ended'

// Auth endpoints whose 401 is an ANSWER (wrong password, wrong code), not an expired session.
const NO_INTERCEPT = ['/api/auth/login', '/api/auth/break-glass', '/api/auth/refresh', '/api/auth/mfa/verify',
  '/api/auth/recover', '/api/auth/password', '/api/auth/logout', '/api/auth/verify-link']

export function clearStoredSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(EXPIRES_KEY)
}

function storeSession(data) {
  localStorage.setItem(TOKEN_KEY, data.token)
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token)
  else localStorage.removeItem(REFRESH_KEY)
  if (data.expires_in) localStorage.setItem(EXPIRES_KEY, String(Date.now() + data.expires_in * 1000))
  else localStorage.removeItem(EXPIRES_KEY)
}

function urlOf(input) {
  if (typeof input === 'string') return input
  if (input && typeof input.url === 'string') return input.url
  return String(input || '')
}

function isApiCall(url) {
  return url.startsWith(`${API_BASE}/api/`) || url.startsWith('/api/')
}

const nativeFetch = (typeof window !== 'undefined' && window.__sempiricalNativeFetch)
  || (typeof window !== 'undefined' ? window.fetch.bind(window) : null)

let refreshInFlight = null

// One refresh at a time, shared by every caller that hit a 401 at the same moment.
export function refreshSession() {
  const refreshToken = localStorage.getItem(REFRESH_KEY)
  if (!refreshToken) return Promise.resolve(null)
  if (!refreshInFlight) {
    refreshInFlight = nativeFetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).then(async res => {
      if (!res.ok) return null
      const data = await res.json()
      storeSession(data)
      return data.token
    }).catch(() => null).finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

// THE ADMIN PORTAL PREVIEW (§0 item 208). While <CustomerView> is mounted, every API call
// carries CUSTOMER_VIEW_HEADER with the previewed customer's id, and the server serves the
// request to a customer of that one customer (security/auth.customer_view_user): the
// customer route allowlist, approved checks only, no internal fields. One place, here in
// the wrapper every fetch goes through, so no portal call can forget it. A call that sets
// the header itself (the tab editor's save sends it empty, to act as the admin) is left as is.
export const CUSTOMER_VIEW_HEADER = 'X-Sempirical-Customer-View'
let customerViewOf = null

function withCustomerView(input, init) {
  const url = urlOf(input)
  if (!customerViewOf || !isApiCall(url) || /\/api\/auth\//.test(url)) return init
  const headers = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined))
  if (headers.has(CUSTOMER_VIEW_HEADER)) return init
  headers.set(CUSTOMER_VIEW_HEADER, customerViewOf)
  return { ...(init || {}), headers }
}

if (typeof window !== 'undefined' && !window.__sempiricalFetchWrapped) {
  window.__sempiricalNativeFetch = nativeFetch
  window.fetch = async (input, rawInit) => {
    const init = withCustomerView(input, rawInit)
    const res = await nativeFetch(input, init)
    const url = urlOf(input)
    if (res.status !== 401 || !isApiCall(url) || NO_INTERCEPT.some(p => url.includes(p))) return res
    const headers = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined))
    if (headers.has('Authorization')) {
      const fresh = await refreshSession()
      if (fresh) {
        headers.set('Authorization', `Bearer ${fresh}`)
        const retry = await nativeFetch(input, { ...(init || {}), headers })
        if (retry.status !== 401) return retry
      }
    }
    clearStoredSession()
    window.dispatchEvent(new Event(SESSION_ENDED_EVENT))
    return res
  }
  window.__sempiricalFetchWrapped = true
}

async function readError(res, fallback) {
  try {
    const body = await res.json()
    return body.detail || fallback
  } catch {
    return fallback
  }
}

function authed(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY)
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  })
}

export function AuthProvider({ children }) {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mfaChallenge, setMfaChallenge] = useState(null) // { token, factorId } while a code is owed
  const refreshTimer = useRef(null)

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) || 0)
    if (!expiresAt || !localStorage.getItem(REFRESH_KEY)) return
    const wait = Math.max(5_000, expiresAt - Date.now() - 60_000)
    refreshTimer.current = setTimeout(async () => {
      const fresh = await refreshSession()
      if (fresh) scheduleRefresh()
      else { clearStoredSession(); setProfile(null) }
    }, wait)
  }, [])

  // The profile always comes from the server's users row.
  const loadProfile = useCallback(async () => {
    try {
      const res = await authed('/api/auth/me')
      if (!res.ok) return false
      const data = await res.json()
      if (data.mfa_required) {
        // A staff session that still owes a TOTP code. If an authenticator is already
        // enrolled, this session is useless until a code is entered at sign-in -- drop it.
        const f = await authed('/api/auth/mfa/factors')
        const factors = f.ok ? ((await f.json()).factors || []) : []
        if (factors.length > 0) { clearStoredSession(); return false }
      }
      setProfile({
        auth_user_id: data.auth_user_id,
        profile_id: data.profile_id,
        email: data.email,
        display_name: data.display_name,
        role: data.role || 'customer',
        customer_ids: data.customer_ids || [],
        permissions: data.permissions || [],
        auth_method: data.auth_method || 'supabase',
        mfa_required: !!data.mfa_required,
      })
      scheduleRefresh()
      return true
    } catch (e) {
      console.warn('Sempirical: loadProfile failed', e.message)
      return false
    }
  }, [scheduleRefresh])

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) { setLoading(false); return }
    const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) || 0)
    const nearExpiry = expiresAt && expiresAt - Date.now() < 60_000 && localStorage.getItem(REFRESH_KEY)
    const start = nearExpiry ? refreshSession() : Promise.resolve(token)
    start.then(t => (t ? loadProfile() : false)).finally(() => setLoading(false))
  }, [loadProfile])

  useEffect(() => {
    const onEnded = () => { setProfile(null); setMfaChallenge(null) }
    window.addEventListener(SESSION_ENDED_EVENT, onEnded)
    return () => {
      window.removeEventListener(SESSION_ENDED_EVENT, onEnded)
      if (refreshTimer.current) clearTimeout(refreshTimer.current)
    }
  }, [])

  const signIn = useCallback(async (email, password, { breakGlass = false } = {}) => {
    try {
      const path = breakGlass ? '/api/auth/break-glass' : '/api/auth/login'
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (res.status === 429) {
        const wait = Number(res.headers.get('Retry-After') || 0)
        return { success: false, error: `Too many attempts. Try again in ${Math.max(1, Math.ceil(wait / 60))} min.` }
      }
      if (!res.ok) return { success: false, error: await readError(res, 'Invalid email or password') }
      const data = await res.json()
      if (data.mfa_required && data.mfa_enrolled) {
        // Hold the half-session in memory only; a code must come next.
        setMfaChallenge({ token: data.token, factorId: data.factor_id })
        return { success: false, mfaRequired: true }
      }
      storeSession(data)
      const ok = await loadProfile()
      if (!ok) return { success: false, error: 'Signed in, but your profile could not be loaded.' }
      return { success: true, profile: { role: data.role || 'customer', customer_ids: data.customer_ids || [] } }
    } catch (e) {
      return { success: false, error: 'Connection error' }
    }
  }, [loadProfile])

  const finishMfa = useCallback(async (token, factorId, code) => {
    const res = await fetch(`${API_BASE}/api/auth/mfa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ factor_id: factorId, code }),
    })
    if (res.status === 429) return { success: false, error: 'Too many wrong codes. Wait 15 minutes.' }
    if (!res.ok) return { success: false, error: await readError(res, 'Invalid verification code.') }
    storeSession(await res.json())
    setMfaChallenge(null)
    const ok = await loadProfile()
    return ok ? { success: true } : { success: false, error: 'Verified, but your profile could not be loaded.' }
  }, [loadProfile])

  // Sign-in code for an already-enrolled authenticator (SignIn's MFA step).
  const verifyMFA = useCallback(async (code) => {
    if (!mfaChallenge) return { success: false, error: 'Sign in again.' }
    return finishMfa(mfaChallenge.token, mfaChallenge.factorId, code)
  }, [mfaChallenge, finishMfa])

  // First-time enrollment (MFASetup page), on the stored aal1 session.
  const enrollMFA = useCallback(async () => {
    const res = await authed('/api/auth/mfa/enroll', { method: 'POST' })
    if (!res.ok) return { success: false, error: await readError(res, 'Could not start MFA setup.') }
    const data = await res.json()
    return { success: true, factorId: data.factor_id, qr: data.qr_code, secret: data.secret }
  }, [])

  const confirmMFAEnrollment = useCallback(async (factorId, code) => {
    return finishMfa(localStorage.getItem(TOKEN_KEY), factorId, code)
  }, [finishMfa])

  const getMFAStatus = useCallback(async () => {
    const res = await authed('/api/auth/mfa/factors')
    if (!res.ok) return { enrolled: false, factors: [], unavailable: true }
    const data = await res.json()
    return { enrolled: (data.factors || []).length > 0, factors: data.factors || [] }
  }, [])

  const unenrollMFA = useCallback(async () => ({
    success: false,
    error: 'An authenticator is removed by an admin (Supabase dashboard -> Users). Ask an admin.',
  }), [])

  const resetPassword = useCallback(async (email) => {
    const res = await fetch(`${API_BASE}/api/auth/recover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) return { success: false, error: await readError(res, 'Password reset is not available.') }
    return { success: true }
  }, [])

  const signOut = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      } catch { /* the local session is cleared regardless */ }
    }
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    clearStoredSession()
    setMfaChallenge(null)
    setProfile(null)
  }, [])

  const role = profile?.role
  const value = {
    profile,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!profile,
    isAdmin: role === 'admin',
    isConsultant: role === 'admin' || role === 'consultant',
    isBD: role === 'business_development',
    isCustomer: role === 'customer',
    // Portal alias
    authProfile: profile,
    session: !!profile,
    // MFA (staff on per-user sign-in)
    mfaChallenge,
    needsMFASetup: !!profile?.mfa_required,
    verifyMFA,
    enrollMFA,
    confirmMFAEnrollment,
    unenrollMFA,
    getMFAStatus,
    checkMFAEnrollment: loadProfile,
    getToken: () => localStorage.getItem(TOKEN_KEY),
    resetPassword,
    // Loading alias
    authLoading: loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// An admin previewing a customer's portal: the children render for the viewer the SERVER
// says this preview is served to (/api/auth/me with the header -- role customer, that
// customer's permissions), and every call they make carries the header. If the server does
// not answer as a customer, the preview fails loudly rather than render the admin view.
// `customerView` tells the portal chrome who is really looking (the tab editor stays admin).
export function CustomerView({ customerId, children }) {
  const auth = useAuth()
  const [viewer, setViewer] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    customerViewOf = customerId
    setViewer(null); setError(null)
    authed('/api/auth/me', { headers: { [CUSTOMER_VIEW_HEADER]: customerId } })
      .then(async res => {
        if (!res.ok) throw new Error(await readError(res, `HTTP ${res.status}`))
        const data = await res.json()
        if (data.role !== 'customer') throw new Error('the server did not serve the customer view')
        if (live) setViewer({ ...auth.profile, role: data.role, customer_ids: data.customer_ids || [],
                              permissions: data.permissions || [] })
      })
      .catch(e => { if (live) setError(e.message || 'Customer view unavailable') })
    return () => { live = false; if (customerViewOf === customerId) customerViewOf = null }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  if (error) return <div className="cportal-root"><div className="cportal-error">Customer preview failed: {error}</div></div>
  if (!viewer) return <div className="cportal-root"><div className="cportal-loading">Loading customer view…</div></div>
  const value = {
    ...auth,
    profile: viewer,
    authProfile: viewer,
    isAdmin: false,
    isConsultant: false,
    isBD: false,
    isCustomer: true,
    customerView: { customerId, staff: auth.profile },
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export default AuthProvider
